import logger from "../../utils/logger";
import { supabase } from './client';

/**
 * Get subscription limits based on plan
 * @param {string} plan - Subscription plan ('free' or 'pro')
 * @param {Object} profile - Optional full profile to check trial expiration
 * @returns {Object} Plan limits configuration
 */
export function getSubscriptionLimits(plan, profile = null) {
  const limits = {
    free: {
      maxProperties: 1,
      maxInspectionsPerProperty: 2, // move-in + move-out per property
      allowedInspectionTypes: ['move-in', 'move-out'],
      canExportPDF: true, // PDF available for all plans
      trialDays: 0,
    },
    starter: {
      maxProperties: 1,  // Same as Free
      maxInspectionsPerProperty: null,  // Unlimited (like Pro)
      allowedInspectionTypes: ['move-in', 'periodic', 'move-out'],  // All types (like Pro)
      canExportPDF: true,
      trialDays: 14,  // 14-day trial (like Pro)
    },
    pro: {
      maxProperties: 5,
      maxInspectionsPerProperty: null, // unlimited
      allowedInspectionTypes: ['move-in', 'periodic', 'move-out'],
      canExportPDF: true,
      trialDays: 14,
    },
  };

  // Normalize plan value: trim whitespace and convert to lowercase
  const normalizedPlan = (plan || '').toString().trim().toLowerCase();

  // If Starter or Pro plan, check if trial has expired
  if ((normalizedPlan === 'starter' || normalizedPlan === 'pro') && profile) {
    const trialStatus = getTrialStatus(profile);

    // If trial expired and no paid subscription, downgrade to free limits
    if (trialStatus.hasTrialed && !trialStatus.isInTrial && !profile.stripe_subscription_id) {
      console.warn('[getSubscriptionLimits] Trial expired, applying free limits');
      return limits.free;
    }
  }

  return limits[normalizedPlan] || limits.free;
}

/**
 * Check if user can create property
 * @param {Object} profile - User profile with subscription_plan and properties_count
 * @param {number} currentPropertyCount - Current number of properties (optional, defaults to profile.properties_count)
 * @returns {Object} { allowed: boolean, reason?: string, requiresUpgrade?: boolean }
 */
export function canCreateProperty(profile, currentPropertyCount = null) {
  if (!profile) {
    return {
      allowed: false,
      reason: 'Profil użytkownika nie został załadowany.',
    };
  }

  // Get limits with trial expiration check
  const limits = getSubscriptionLimits(profile.subscription_plan, profile);
  const maxProperties = limits.maxProperties;

  // Use provided count or fallback to profile count
  const propertyCount = currentPropertyCount !== null ? currentPropertyCount : profile.properties_count;

  if (propertyCount >= maxProperties) {
    let reason;
    let upgradeContext = 'property_limit'; // Used by modal to show only relevant plans
    if (maxProperties === 1) {
      if (profile.subscription_plan === 'free') {
        reason = 'Plan darmowy pozwala na 1 nieruchomość. Przejdź na plan Pro (49 PLN/mc) dla 5 nieruchomości.';
      } else if (profile.subscription_plan === 'starter') {
        reason = 'Plan Starter pozwala na 1 nieruchomość. Przejdź na plan Pro (49 PLN/mc) dla 5 nieruchomości.';
      } else {
        reason = 'Osiągnięto limit 1 nieruchomości.';
      }
    } else {
      reason = `Osiągnięto limit ${maxProperties} nieruchomości dla planu Pro.`;
    }

    return {
      allowed: false,
      reason,
      requiresUpgrade: maxProperties < 5,  // Upgrade możliwy jeśli nie Pro
      upgradeContext,  // Modal uses this to decide which plans to show
    };
  }

  return { allowed: true };
}

/**
 * Check if user can create inspection of given type
 * @param {Object} profile - User profile with subscription_plan
 * @param {string} inspectionType - Type: 'move-in', 'periodic', or 'move-out'
 * @returns {Object} { allowed: boolean, reason?: string, requiresUpgrade?: boolean }
 */
export function canCreateInspection(profile, inspectionType) {
  if (!profile) {
    return {
      allowed: false,
      reason: 'Profil użytkownika nie został załadowany.',
    };
  }

  logger.log('[canCreateInspection] Checking:', {
    inspectionType,
    subscriptionPlan: profile.subscription_plan,
    profileData: profile,
  });

  const limits = getSubscriptionLimits(profile.subscription_plan, profile);

  logger.log('[canCreateInspection] Limits:', {
    allowedInspectionTypes: limits.allowedInspectionTypes,
    isAllowed: limits.allowedInspectionTypes.includes(inspectionType),
  });

  if (!limits.allowedInspectionTypes.includes(inspectionType)) {
    return {
      allowed: false,
      reason:
        'Inspekcje okresowe dostępne w planach Starter (29 PLN/mc, 1 nieruchomość) i Pro (49 PLN/mc, 5 nieruchomości). Rozpocznij 14-dniowy okres próbny za darmo.',
      requiresUpgrade: true,
      upgradeContext: 'features',  // Show both plans (Starter has this feature)
    };
  }

  return { allowed: true };
}

/**
 * Check if user can create inspection for specific property
 * Takes into account per-property inspection limits for free users
 * @param {Object} profile - User profile
 * @param {number} currentInspectionCount - Current inspection count for this property
 * @returns {Object} { allowed: boolean, reason?: string, requiresUpgrade?: boolean }
 */
export function canCreateInspectionForProperty(profile, currentInspectionCount) {
  if (!profile) {
    return {
      allowed: false,
      reason: 'Profil użytkownika nie został załadowany.',
    };
  }

  // Get limits based on subscription plan (with trial expiration check)
  const limits = getSubscriptionLimits(profile.subscription_plan, profile);
  const maxInspections = limits.maxInspectionsPerProperty;

  // Users with null limit = unlimited (Pro users with active subscription)
  if (maxInspections === null || maxInspections === undefined) {
    return { allowed: true };
  }

  // Check limit for free users and expired trial users
  if (currentInspectionCount >= maxInspections) {
    return {
      allowed: false,
      reason: `Plan darmowy pozwala na ${maxInspections} inspekcje na nieruchomość (wprowadzenie + wyprowadzka). Przejdź na plan Pro, aby dodać więcej.`,
      requiresUpgrade: true,
    };
  }

  return { allowed: true };
}

/**
 * Check if user can export PDF
 * NOTE: PDF export is now available for all plans (Free + Pro)
 * @param {Object} profile - User profile with subscription_plan
 * @returns {Object} { allowed: boolean }
 */
export function canExportPDF(profile) {
  // PDF available for all users
  return { allowed: true };
}

/**
 * Start Pro trial for authenticated user
 * @returns {Promise<Object>} { success: boolean, error?: string, trialEndsAt?: string }
 */
export async function startProTrial() {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase.rpc('start_pro_trial', {
      user_id: user.id,
    });

    if (error) throw error;

    if (!data || !data.success) {
      return { success: false, error: data?.error || 'Unknown error' };
    }

    return { success: true, error: null, trialEndsAt: data.trial_ends_at };
  } catch (error) {
    console.error('Error starting trial:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get trial status for user
 * @param {Object} profile - User profile with trial fields
 * @returns {Object} { hasTrialed: boolean, isInTrial: boolean, daysRemaining: number, trialEndsAt?: string }
 */
export function getTrialStatus(profile) {
  if (!profile) {
    return {
      hasTrialed: false,
      isInTrial: false,
      daysRemaining: 0,
    };
  }

  // Check if user has trial for Starter or Pro plan
  const hasTrialPlan = profile.subscription_plan === 'starter' || profile.subscription_plan === 'pro';

  if (!profile.trial_started_at || !hasTrialPlan) {
    return {
      hasTrialed: !!profile.trial_started_at,
      isInTrial: false,
      daysRemaining: 0,
    };
  }

  const trialEnd = new Date(profile.trial_ends_at);
  const now = new Date();
  const isInTrial = trialEnd > now && !profile.stripe_subscription_id;
  const daysRemaining = isInTrial
    ? Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24))
    : 0;

  return {
    hasTrialed: true,
    isInTrial,
    daysRemaining,
    trialEndsAt: profile.trial_ends_at,
  };
}

/**
 * Get photo limits based on subscription plan
 * @param {string} plan - Subscription plan ('free' or 'pro')
 * @returns {Object} Photo limits configuration
 */
export function getPhotoLimits(plan) {
  const limits = {
    free: {
      maxPhotosPerInspection: 5,   // Total across all rooms/items
      maxPhotosPerItem: null,       // No per-item limit
    },
    starter: {
      maxPhotosPerInspection: null, // Unlimited (like Pro)
      maxPhotosPerItem: 4,          // 4 per item (like Pro)
    },
    pro: {
      maxPhotosPerInspection: null, // No total limit
      maxPhotosPerItem: 4,          // Max 4 per item
    },
  };

  const normalizedPlan = (plan || '').toString().trim().toLowerCase();
  return limits[normalizedPlan] || limits.free;
}

/**
 * Check if user can add photo to item
 * @param {Object} profile - User profile with subscription_plan
 * @param {Object} inspection - Current inspection with rooms/items/photos
 * @param {Array} itemPhotos - Photos array of current item
 * @returns {Object} { allowed: boolean, reason?: string, requiresUpgrade?: boolean, currentCount?: number, maxCount?: number }
 */
export function canAddPhotoToItem(profile, inspection, itemPhotos) {
  if (!profile) {
    return {
      allowed: false,
      reason: 'Profil użytkownika nie został załadowany.',
    };
  }

  const limits = getPhotoLimits(profile.subscription_plan);

  // Plan Free: check total photos in entire inspection
  if (profile.subscription_plan === 'free') {
    const totalPhotos = inspection.rooms.reduce((total, room) =>
      total + room.items.reduce((roomTotal, item) =>
        roomTotal + item.photos.length, 0
      ), 0
    );

    if (totalPhotos >= limits.maxPhotosPerInspection) {
      return {
        allowed: false,
        reason: `Plan darmowy pozwala na maksymalnie ${limits.maxPhotosPerInspection} zdjęć na całą inspekcję. Przejdź na plan Pro dla nieograniczonej liczby zdjęć (do 4 na element).`,
        requiresUpgrade: true,
        currentCount: totalPhotos,
        maxCount: limits.maxPhotosPerInspection,
      };
    }
  }

  // Plan Starter or Pro: check photos per item
  if (profile.subscription_plan === 'starter' || profile.subscription_plan === 'pro') {
    if (itemPhotos.length >= limits.maxPhotosPerItem) {
      return {
        allowed: false,
        reason: `Osiągnięto limit ${limits.maxPhotosPerItem} zdjęć dla tego elementu.`,
        requiresUpgrade: false,
        currentCount: itemPhotos.length,
        maxCount: limits.maxPhotosPerItem,
      };
    }
  }

  return { allowed: true };
}
